import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import type { EditorPlanExercise } from './types';

interface ExerciseRowProps {
  exercise: EditorPlanExercise;
  isDisabled: boolean;
  isReadOnly: boolean;
  onChange: (exercise: EditorPlanExercise) => void;
  onRemove: () => void;
}

export function ExerciseRow({
  exercise,
  isDisabled,
  isReadOnly,
  onChange,
  onRemove,
}: ExerciseRowProps) {
  const { t } = useTranslation('plans');

  return (
    <div className="border border-zooborder rounded-2xl p-4 bg-zoooffwhite">
      {isReadOnly ? (
        <>
          <p className="text-sm font-bold text-zooindigo">{exercise.name}</p>
          {exercise.instructions.trim().length === 0 ? null : (
            <p className="text-xs text-zoomuted mt-2 whitespace-pre-wrap">
              {exercise.instructions}
            </p>
          )}
          {exercise.frequency.trim().length === 0 ? null : (
            <p className="text-xs text-zooviolet mt-1">
              {t('editor.exerciseFrequency')}: {exercise.frequency}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <label className="block text-xs font-semibold text-zoomuted flex-1 min-w-0">
              {t('editor.exerciseName')}
              <input
                type="text"
                value={exercise.name}
                disabled={isDisabled}
                placeholder={t('editor.exerciseNamePlaceholder')}
                onChange={function change(event) {
                  onChange({ ...exercise, name: event.target.value });
                }}
                className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
              />
            </label>
            <Button
              variant="destructiveGhost"
              size="icon"
              onClick={onRemove}
              disabled={isDisabled}
              aria-label={t('editor.removeExercise')}
              className="shrink-0 mt-5"
            >
              <Icon name="Trash" />
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 mt-3">
            <label className="block text-xs font-semibold text-zoomuted">
              {t('editor.exerciseInstructions')}
              <textarea
                value={exercise.instructions}
                disabled={isDisabled}
                rows={2}
                placeholder={t('editor.exerciseInstructionsPlaceholder')}
                onChange={function change(event) {
                  onChange({ ...exercise, instructions: event.target.value });
                }}
                className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
              />
            </label>
            <label className="block text-xs font-semibold text-zoomuted">
              {t('editor.exerciseFrequency')}
              <input
                type="text"
                value={exercise.frequency}
                disabled={isDisabled}
                placeholder={t('editor.exerciseFrequencyPlaceholder')}
                onChange={function change(event) {
                  onChange({ ...exercise, frequency: event.target.value });
                }}
                className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
              />
            </label>
          </div>
        </>
      )}

      {exercise.compliance === null ? null : (
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge
            tone={exercise.compliance.total > 0 ? 'success' : 'muted'}
            size="sm"
            className="text-[11px]"
          >
            {exercise.compliance.total > 0
              ? t('editor.complianceDoneCount', { count: exercise.compliance.total })
              : t('editor.complianceNotStarted')}
          </Badge>
          {exercise.compliance.lastCompletedOn === null ? null : (
            <Badge tone="muted" size="sm" className="text-[11px]">
              {t('editor.complianceLastDone', {
                date: formatIsoDateToDayAndMonth(exercise.compliance.lastCompletedOn),
              })}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
